import React, { useState } from "react";
import { motion } from "framer-motion";
import { TrendingUp, Clock, Phone } from "lucide-react";
import { EmployeeAvatar } from "@/components/EmployeeAvatar";
import { StatusBadge } from "@/components/StatusBadge";
import { EmployeePanel } from "@/components/EmployeePanel";
import { VoiceCall } from "@/components/VoiceCall";
import { useStore } from "@/lib/store";
import { useNavigate } from "react-router-dom";

function Sparkline({ data, color = "#00E5FF" }) {
  const max = Math.max(...data), min = Math.min(...data);
  const pts = data.map((v, i) => `${(i / (data.length - 1)) * 100},${28 - ((v - min) / (max - min || 1)) * 24 - 2}`).join(" ");
  return (
    <svg viewBox="0 0 100 28" className="h-7 w-24" preserveAspectRatio="none">
      <polyline points={pts} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export default function MyEmployees() {
  const { state } = useStore();
  const [panel, setPanel] = useState(null);
  const [callId, setCallId] = useState(null);
  const navigate = useNavigate();
  const callEmp = state.employees.find((e) => e.id === callId);

  return (
    <div>
      {panel && <EmployeePanel employeeId={panel.id} initialMode={panel.mode} onClose={() => setPanel(null)} />}
      {callEmp && <VoiceCall employee={callEmp} onClose={() => setCallId(null)} onSwitchToText={() => { setCallId(null); setPanel({ id: callEmp.id, mode: "talk" }); }} />}
      <div className="mb-6 flex items-end justify-between">
        <div>
          <h1 className="font-display text-3xl font-semibold text-white sm:text-4xl">My Associates</h1>
          <p className="mt-1 text-sm text-slate-400">{state.employees.length} AI associates on staff</p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        {state.employees.map((e, i) => (
          <motion.div key={e.id} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }} className="glass group rounded-2xl p-5 transition-colors hover:border-[#00E5FF]/30" data-testid={`employee-card-${e.id}`}>
            <div className="flex items-start gap-4">
              <EmployeeAvatar employee={e} size={64} onClick={() => setPanel({ id: e.id, mode: "office" })} />
              <div className="min-w-0 flex-1">
                <button onClick={() => navigate(`/employees/${e.id}`)} className="text-left" data-testid={`employee-name-${e.id}`}>
                  <div className="font-display text-lg font-semibold text-white group-hover:text-[#00E5FF]">{e.name}</div>
                </button>
                <div className="text-sm text-slate-400">{e.title}</div>
                <div className="mt-2"><StatusBadge status={e.status} /></div>
              </div>
            </div>

            <div className="mt-4 rounded-xl border border-white/8 bg-[#030A16]/40 p-3 text-xs text-slate-300">
              <span className="text-slate-500">Now: </span>{e.assignment}
            </div>

            <div className="mt-4 grid grid-cols-3 gap-2 text-center">
              <div><div className="font-display text-lg text-white">{e.tasksToday}</div><div className="text-[10px] text-slate-500">Today</div></div>
              <div className="flex flex-col items-center"><Clock size={14} className="text-[#22D3EE]" /><div className="font-display text-lg text-white">{e.hoursSaved}</div><div className="text-[10px] text-slate-500">Hrs saved</div></div>
              <div className="flex flex-col items-center"><TrendingUp size={14} className="text-[#8A2BE2]" /><Sparkline data={e.trend} color="#8A2BE2" /></div>
            </div>

            <div className="mt-4 flex gap-2">
              <button onClick={() => setCallId(e.id)} className="flex flex-1 items-center justify-center gap-1.5 rounded-lg bg-gradient-to-r from-[#00E5FF] to-[#8A2BE2] px-3 py-2 text-xs font-semibold text-[#030A16]" data-testid={`talk-${e.id}`}><Phone size={14} /> Voice</button>
              <button onClick={() => navigate(`/employees/${e.id}`)} className="flex-1 rounded-lg border border-white/10 px-3 py-2 text-xs text-slate-200 hover:border-[#00E5FF]/40 hover:text-white" data-testid={`profile-${e.id}`}>Full profile</button>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
